import { acceptHMRUpdate, defineStore } from 'pinia'
import type { BaseAddress, CompleteAddress } from '~/types/utils'
import { noResponseFetch } from '~/utils/webSockets'
import {
  type CookieCategory,
  type CookieDecision,
  type DetailUser,
  type NewContactDetails,
  type PDFResponse,
  type ThinkificSignupResponse,
  type UpdatedUser,
  type UpdatedUserWithId,
  type UserWalletPaginationResponse
} from '~/types/users'
import type { UpdatedFoodie, UpdatedFoodieWithId } from '~/types/foodies'
import type {
  BaseUpdateCook,
  MissingCookDetailsParams,
  MissingCookDetailsResponse,
  WebsiteCookResult
} from '~/types/cooks'
import type { RoleType } from '~/types/auth'
import type { Match } from '~/types/matches'
import type {
  AddBalanceParams,
  BalancePageFilters,
  ExtendedUserWallet,
  UserInfoForPaymentOverview,
  UserWallet,
  WalletsForPeriodParams
} from '~/types/userWallets'
import type {
  CreateMatchMealParams,
  MatchMeal,
  MatchMealId,
  MatchMealPDF
} from '~/types/matchMeals'
import statusMessages from '/@lang/status-messages.json'
import type { SearchAddressResponse } from '~/types/registration'
import type { Dayjs } from 'dayjs'
import type { UpdateMealWishParams } from '~/types/meal-wishes'
import type { BaseCaretaker, Caretaker } from '~/types/caretakers'
import { cloneDeep, uniqBy } from 'es-toolkit'
import type { UploadableFile } from '~/utils/files/uploadableFile'
import type { ApiResponse } from '#shared/types/utils'
import type { FoodieId } from '#shared/types/foodies.ts'
import { usePersist } from '~/plugins/pinia.ts'

interface State {
  specificUser?: DetailUser
  internalUserWallets: UserWalletPaginationResponse
  userPaymentInformation?: UserInfoForPaymentOverview
  balancePageFilters: BalancePageFilters

  visitorId?: string
}

const userStatusMessages = statusMessages['user-status-messages']

const defaultWalletEntryValues = {
  data: [],
  links: {
    next: null
  },
  meta: {
    perPage: 0,
    total: 0,
    currentPage: 1
  }
}

const defaultBalanceFilters: BalancePageFilters = {
  months: [],
  year: undefined,
  customUserWalletTypes: [],
  oppositeRoleIds: []
}

export const useUsersStore = defineStore('users', {
  state: (): State => ({
    specificUser: undefined,
    internalUserWallets: defaultWalletEntryValues,
    userPaymentInformation: undefined,
    balancePageFilters: defaultBalanceFilters,

    visitorId: undefined
  }),
  persist: usePersist<State>({
    storage: piniaPluginPersistedstate.localStorage(),
    pick: ['visitorId']
  }),
  logout: {
    pick: ['visitorId']
  },
  actions: {
    resetUser() {
      this.specificUser = undefined
    },

    resetWalletEntries() {
      this.internalUserWallets = cloneDeep(defaultWalletEntryValues)
    },

    async fetchUser(): Promise<boolean> {
      try {
        const { data } = await this.api.get<ApiResponse<DetailUser>>('user/fetch')
        this.specificUser = data
        return true
      } catch {
        addToast().error(userStatusMessages[this.currentLocale].error['fetch-user'])
        return false
      }
    },

    async fetchUpdateUser(): Promise<false | UpdatedUserWithId> {
      try {
        const response = await this.api.get<ApiResponse<UpdatedUserWithId>>('user/update')
        return response.data
      } catch {
        addToast().error(userStatusMessages[this.currentLocale].error['fetch-user'])
        return false
      }
    },

    async fetchUpdateCook(): Promise<WebsiteCookResult | false> {
      try {
        const response = await this.api.get<ApiResponse<WebsiteCookResult>>('cook')
        return response.data
      } catch {
        addToast().error(userStatusMessages[this.currentLocale].error['fetch-cook'])
        return false
      }
    },

    async fetchMissingCookDetails(): Promise<false | MissingCookDetailsResponse> {
      try {
        const response =
          await this.api.get<ApiResponse<MissingCookDetailsResponse>>(
            'cook/missing-details'
          )
        return response.data
      } catch {
        addToast().error(
          userStatusMessages[this.currentLocale].error['fetch-missing-cook-details']
        )
        return false
      }
    },

    async fetchUpdatedFoodie(): Promise<false | UpdatedFoodieWithId> {
      try {
        const response = await this.api.get<ApiResponse<UpdatedFoodieWithId>>('foodie')
        return response.data
      } catch {
        addToast().error(userStatusMessages[this.currentLocale].error['fetch-foodie'])
        return false
      }
    },

    async fetchUpdatedCaretaker(): Promise<false | Caretaker> {
      try {
        const response = await this.api.get<ApiResponse<Caretaker>>('caretaker')
        return response.data
      } catch {
        addToast().error(userStatusMessages[this.currentLocale].error['fetch-caretaker'])
        return false
      }
    },

    async getMatchMeals(): Promise<false | MatchMeal[]> {
      try {
        const response = await this.api.get<ApiResponse<MatchMeal[]>>('user/meals')
        return response.data
      } catch {
        addToast().error(
          userStatusMessages[this.currentLocale].error['fetch-match-meals']
        )
        return false
      }
    },

    async getMatchMealsPDFData(
      startDate: Dayjs,
      endDate: Dayjs
    ): Promise<MatchMealPDF[] | false> {
      try {
        const response = await this.api.post<ApiResponse<MatchMealPDF[]>>(
          'user/meals/pdf',
          {
            startDate,
            endDate
          }
        )

        return response.data
      } catch {
        addToast().error(
          userStatusMessages[this.currentLocale].error['fetch-match-meals']
        )
        return false
      }
    },

    async getMatches(): Promise<Match[] | false> {
      try {
        const response = await this.api.get<ApiResponse<Match[]>>('user/matches')
        return response.data
      } catch {
        addToast().error(userStatusMessages[this.currentLocale].error['fetch-matches'])
        return false
      }
    },

    async createMeal(params: CreateMatchMealParams): Promise<MatchMeal | false> {
      try {
        const response = await this.api.put<ApiResponse<MatchMeal>>('user/meal', params)
        addToast().success(userStatusMessages[this.currentLocale].success['create-meal'])
        return response.data
      } catch {
        addToast().error(userStatusMessages[this.currentLocale].error['create-meal'])
        return false
      }
    },

    async updateContactDetails(contactDetails: NewContactDetails): Promise<boolean> {
      return noResponseFetch.put({
        url: '/user/user-profile',
        payload: contactDetails,
        success: userStatusMessages[this.currentLocale].success['update-contact-details'],
        error: userStatusMessages[this.currentLocale].error['update-contact-details']
      })
    },

    async updateFetchedUser(userParams: UpdatedUser): Promise<boolean> {
      return noResponseFetch.put({
        url: '/user/update',
        payload: {
          ...userParams,
          locale: this.currentLocale
        },
        success: userStatusMessages[this.currentLocale].success['update-user'],
        error: userStatusMessages[this.currentLocale].error['update-user']
      })
    },

    async updateCookUnavailableUntil(unavailableUntil: Dayjs | null): Promise<boolean> {
      return noResponseFetch.put({
        url: '/cook/unavailable-for-matches',
        payload: { unavailableForMatchesUntil: unavailableUntil },
        error: userStatusMessages[this.currentLocale].error['update-cook-unavailability']
      })
    },

    async updateCookMealWishes(mealWishes: UpdateMealWishParams): Promise<boolean> {
      return noResponseFetch.put({
        url: '/cook/meal-wishes',
        payload: mealWishes,
        error: userStatusMessages[this.currentLocale].error['update-meal-wishes-failed']
      })
    },

    async updateCook(cook: BaseUpdateCook): Promise<boolean> {
      return noResponseFetch.put({
        url: '/cook/update',
        payload: cook,
        success: userStatusMessages[this.currentLocale].success['update-cook'],
        error: userStatusMessages[this.currentLocale].error['update-cook']
      })
    },

    async updateFetchedFoodie(foodie: UpdatedFoodie): Promise<boolean> {
      return noResponseFetch.put({
        url: '/foodie/update',
        payload: foodie,
        success: userStatusMessages[this.currentLocale].success['update-foodie'],
        error: userStatusMessages[this.currentLocale].error['update-foodie']
      })
    },

    async updateFetchedCaretaker(
      caretaker: BaseCaretaker,
      foodieId: FoodieId
    ): Promise<boolean> {
      return noResponseFetch.put({
        url: '/caretaker/update',
        payload: {
          ...caretaker,
          foodieId: foodieId
        },
        success: userStatusMessages[this.currentLocale].success['update-caretaker'],
        error: userStatusMessages[this.currentLocale].error['update-caretaker']
      })
    },

    async updateMeal(
      mealId: MatchMealId,
      params: CreateMatchMealParams
    ): Promise<boolean> {
      return noResponseFetch.put({
        url: `/user/meal/${mealId}`,
        payload: params,
        success: userStatusMessages[this.currentLocale].success['update-meal'],
        error: userStatusMessages[this.currentLocale].error['update-meal']
      })
    },

    async updateMealDescription(
      mealId: MatchMealId,
      mealDescription: string | null
    ): Promise<boolean> {
      return noResponseFetch.put({
        url: `/user/meal/${mealId}/update/meal-description`,
        payload: { mealDescription },
        success: userStatusMessages[this.currentLocale].success['update-meal'],
        error: userStatusMessages[this.currentLocale].error['update-meal']
      })
    },

    async updateMissingCookDetails(params: MissingCookDetailsParams): Promise<boolean> {
      return noResponseFetch.put({
        url: '/cook/missing-details',
        payload: params,
        success: userStatusMessages[this.currentLocale].success['update-cook'],
        error: userStatusMessages[this.currentLocale].error['update-cook']
      })
    },

    async cancelMeal(mealId: MatchMealId): Promise<boolean> {
      return noResponseFetch.delete({
        url: `/user/meal/${mealId}`,
        success: userStatusMessages[this.currentLocale].success['cancel-meal'],
        error: userStatusMessages[this.currentLocale].error['cancel-meal']
      })
    },

    async fetchUserWalletsAfterReset(): Promise<UserWallet[] | false> {
      try {
        const { data } = await this.api.get<ApiResponse<UserWallet[]>>(
          'wallet/balance-after-reset'
        )
        return data
      } catch {
        addToast().error(userStatusMessages[this.currentLocale].error['fetch-wallets'])
        return false
      }
    },

    async fetchPaymentInformation(): Promise<boolean> {
      if (this.userPaymentInformation) {
        return true
      }

      try {
        this.userPaymentInformation = await this.api.get<UserInfoForPaymentOverview>(
          'wallet/payment-information'
        )
        return true
      } catch {
        return false
      }
    },

    async getWalletEntries(): Promise<boolean> {
      const pagesAmount = Math.ceil(
        this.internalUserWallets.meta.total / this.internalUserWallets.meta.perPage
      )

      if (this.internalUserWallets.meta.currentPage >= pagesAmount) {
        return false
      }

      try {
        const url =
          this.internalUserWallets.links.next?.replace('http://tg-backend/api', '') ||
          '/wallet/entries'
        const response = await this.api.get<UserWalletPaginationResponse>(url)

        const uniqData = uniqBy(
          [...this.internalUserWallets.data, ...response.data],
          (u) => u.id
        )

        this.internalUserWallets = {
          ...response,
          data: uniqData
        }

        return true
      } catch {
        return false
      }
    },

    async addToWallet(params: AddBalanceParams): Promise<string | false> {
      const windowOpen = window.open('', '_blank')

      try {
        const { redirectUrl } = await this.api.put<{ redirectUrl: string | null }>(
          '/wallet/add-balance',
          params
        )

        if (redirectUrl && windowOpen) {
          windowOpen.location.href = redirectUrl
          return redirectUrl
        } else {
          windowOpen?.close()
          return false
        }
      } catch {
        return false
      }
    },

    async validateEmail(emailAddress: string): Promise<boolean | 'exists'> {
      try {
        await this.api.post('validate-email', { email: emailAddress })
        return true
      } catch (error: any) {
        if (error && error?._data) {
          const errors = error?._data.message
          if (errors === 'err_email_exists') {
            return 'exists'
          }
        }
        return false
      }
    },

    async fetchWalletsForPeriod(
      params: WalletsForPeriodParams
    ): Promise<UserWallet[] | false> {
      try {
        const { data } = await this.api.post<ApiResponse<UserWallet[]>>(
          'wallet/wallets-for-period',
          params
        )

        return data
      } catch {
        addToast().error(userStatusMessages[this.currentLocale].error['fetch-wallets'])
        return false
      }
    },

    async searchAddress(address: BaseAddress): Promise<CompleteAddress | false> {
      try {
        const result = await this.api.post<SearchAddressResponse>(
          '/search/address',
          address
        )
        return result.address as CompleteAddress
      } catch {
        return false
      }
    },

    async searchHouseNumbers(address: BaseAddress): Promise<string[] | boolean> {
      try {
        const response = await this.api.post<ApiResponse<string[]>>(
          '/search/autocomplete',
          address
        )
        const houseNumbers = response.data

        if (houseNumbers.length > 1) {
          return houseNumbers
        } else if (houseNumbers.length === 1) {
          return true
        }
        return false
      } catch {
        return false
      }
    },

    async declareHolidays(startDate: Dayjs, endDate: Dayjs): Promise<boolean> {
      try {
        await this.api.post('/user/matches/holiday-meals', { startDate, endDate })
        return true
      } catch {
        addToast().error(
          userStatusMessages[this.currentLocale].error['create-match-meals']
        )
        return false
      }
    },

    async signupForThinkific(): Promise<string | false> {
      try {
        const result = await this.api.put<ThinkificSignupResponse>(
          '/user/thinkific-signup'
        )

        if ('signInUrl' in result) {
          return result.signInUrl
        } else if (result.userExists && result.linked) {
          return 'https://academie.thuisgekookt.nl'
        } else {
          addToast().error(
            userStatusMessages[this.currentLocale].error[
              'thinkific-signup-account-exists'
            ]
          )
          return false
        }
      } catch {
        addToast().error(
          userStatusMessages[this.currentLocale].error['thinkific-signup-account-failed']
        )
        return false
      }
    },

    async getThinkificExpressSignInUrl(): Promise<string | false> {
      try {
        const result = await this.api.get<{ signInUrl: string }>('/user/thinkific-signup')
        return result.signInUrl
      } catch {
        addToast().error(
          userStatusMessages[this.currentLocale].error['thinkific-signup-account-failed']
        )
        return false
      }
    },

    async uploadVog(vogFile: UploadableFile): Promise<boolean> {
      const formData = new FormData()
      formData.append('file', vogFile.file, vogFile.file.name)

      vogFile.status = 'loading'

      try {
        const { status } = await this.api.post<PDFResponse>('user/upload-vog', formData, {
          headers: new Headers({ Accept: 'application/json' })
        })

        vogFile.status = status === 'ok'
        addToast().success(userStatusMessages[this.currentLocale].success['upload-vog'])
        return true
      } catch {
        vogFile.status = false
        addToast().error(userStatusMessages[this.currentLocale].error['upload-vog'])
        return false
      }
    },

    async recordConsentLog(
      choice: CookieDecision,
      categories: Record<CookieCategory, CookieDecision>
    ): Promise<boolean> {
      try {
        if (!this.visitorId) {
          this.visitorId = crypto.randomUUID()
        }

        await this.api.post('consent-log', {
          visitorId: this.visitorId,
          choice,
          categories
        })
        return true
      } catch (e) {
        console.error(e)
        return false
      }
    }
  },
  getters: {
    userWallets: (state: State): ExtendedUserWallet[] => {
      let balance = state.specificUser?.balance ?? 0

      return state.internalUserWallets.data.map((wallet) => {
        const result = {
          ...wallet,
          balanceAfterMutation: balance
        }

        balance -= wallet.totalAmount

        return result
      })
    },

    user: (state: State): DetailUser => state.specificUser!,

    hasRole:
      (state: State) =>
      (roleType: RoleType): boolean => {
        if (!state.specificUser) {
          return false
        }

        // Stuff crashes if you use utils/helpers.ts::hasRole here
        return state.specificUser.roles.some((role) => role.role === roleType)
      }
  }
})

/**
 * Pinia supports Hot Module replacement, so you can edit your stores and
 * interact with them directly in your app without reloading the page.
 *
 * @see https://pinia.esm.dev/cookbook/hot-module-replacement.html
 * @see https://vitejs.dev/guide/api-hmr.html
 */
if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useUsersStore, import.meta.hot))
}
